import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../config/api';
import './ResetPassword.css'; 
import { CheckCircle, Info, Loader2, Eye, EyeOff } from 'lucide-react';

const ResetPassword = () => {
  // State
  const [step, setStep] = useState(1); // 1: Email, 2: Verification
  const [email, setEmail] = useState('');
  const [codeDigits, setCodeDigits] = useState(['', '', '', '', '', '']);
  
  // Password fields (only needed if actually resetting password)
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  
  // UI State
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [resendDisabled, setResendDisabled] = useState(false);
  
  const navigate = useNavigate();

  useEffect(() => {
    document.body.classList.add('reset-password-page');
    return () => {
      document.body.classList.remove('reset-password-page');
    };
  }, []);

  // --- Handlers ---
  const handleCodeChange = (index, value) => {
    if (value.length > 1) return;
    const newCode = [...codeDigits];
    newCode[index] = value;
    setCodeDigits(newCode);
    if (value && index < 5) document.getElementById(`code-${index + 1}`)?.focus();
  };

  const handleCodeKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !codeDigits[index] && index > 0) {
      document.getElementById(`code-${index - 1}`)?.focus();
    }
  };

  // --- API ---
  const handleRequestReset = async (e) => {
    e.preventDefault();
    setLoading(true); setError(''); setSuccessMessage('');
    try {
      const response = await fetch(`${API_BASE_URL}/api/password-reset/request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      const data = await response.json();
      if (data.success) {
        setStep(2);
        setSuccessMessage(''); 
      } else {
        throw new Error(data.message || 'Failed to send reset code');
      }
    } catch (err) { setError(err.message || 'An error occurred'); } 
    finally { setLoading(false); }
  };

  const handleResendCode = async () => {
    if (resendDisabled) return;
    setLoading(true); setError(''); setSuccessMessage(''); setResendDisabled(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/password-reset/resend-code`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      const data = await response.json();
      if (data.success) {
        setSuccessMessage('New code sent!');
        setTimeout(() => setResendDisabled(false), 60000);
      } else { throw new Error(data.message || 'Failed to resend code'); }
    } catch (err) { setError(err.message || 'An error occurred'); setResendDisabled(false); } 
    finally { setLoading(false); }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) { setError('Passwords do not match'); return; }
    const fullCode = codeDigits.join('');
    if (fullCode.length !== 6) { setError('Please enter the full 6-digit code'); return; }

    setLoading(true); setError(''); setSuccessMessage('');
    try {
      const response = await fetch(`${API_BASE_URL}/api/password-reset/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, code: fullCode, new_password: newPassword }),
      });
      const data = await response.json();
      if (data.success) {
        setSuccessMessage('Success! Redirecting...');
        setTimeout(() => navigate('/login'), 2000);
      } else { throw new Error(data.message || 'Failed to reset password'); }
    } catch (err) { setError(err.message || 'An error occurred'); } 
    finally { setLoading(false); }
  };

  return (
    <div className="reset-page-wrapper">
      
      {/* Header */}
      <div className="reset-header-bar">
         <div className="header-logo">
             <img src="/images/NexOpta-Logo.png" alt="Nexopta" />
         </div>
         <div className="step-indicator">
             {step === 1 ? 'Step 1 of 2' : 'Step 2 of 2'}
         </div>
      </div>
      
      {/* Progress Bar */}
      <div className="progress-container">
          <div className="progress-bar" style={{ width: step === 1 ? '50%' : '100%' }}></div>
      </div>

      <div className="reset-content-row">
        
        {/* LEFT COLUMN */}
        <div className="reset-left-col">
          
          {/* Back Link - Only on Step 2 */}
          {step === 2 && (
            <button onClick={() => setStep(1)} className="back-link-top">
               &larr; Back to details
            </button>
          )}

          <div className="text-header">
             <h2>{step === 1 ? 'Reset Password' : 'Check your email'}</h2>
             <p className="subtitle">
               {step === 1 
                 ? "Enter your email address to begin the reset process." 
                 : <>We've sent a 6-digit verification code to <span className="highlight-email">{email}</span></>}
             </p>
          </div>

          {error && <div className="status-banner error"><Info size={18}/> <span>{error}</span></div>}
          {successMessage && <div className="status-banner success"><CheckCircle size={18}/> <span>{successMessage}</span></div>}

          {/* --- STEP 1 RENDER --- */}
          {step === 1 ? (
             <>
               <form onSubmit={handleRequestReset} className="main-form">
                  <div className="input-group">
                     <label>Business Email</label>
                     <input 
                        type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                        placeholder="you@company.com" required disabled={loading}
                     />
                  </div>
                  <button type="submit" className="primary-btn" disabled={loading}>
                     {loading ? <Loader2 className="spinner" size={18}/> : 'Send Reset Code'}
                  </button>
               </form>
               
               {/* Login link acts as footer for Step 1 ONLY */}
               <div className="footer-login-link">
                  Already have an account? <Link to="/login">Log In</Link>
               </div>
             </>
          ) : (
             /* --- STEP 2 RENDER --- */
             <div className="step2-container">
                
                {/* White Card for Inputs */}
                <div className="verification-card">
                   <div className="code-grid">
                      {codeDigits.map((digit, idx) => (
                        <input key={idx} id={`code-${idx}`} type="text" maxLength={1} className="code-box"
                          value={digit} onChange={(e) => handleCodeChange(idx, e.target.value)}
                          onKeyDown={(e) => handleCodeKeyDown(idx, e)} disabled={loading} autoComplete="off"
                        />
                      ))}
                   </div>
                   
                   {/* Password Fields */}
                   <div className="password-fields">
                       <div className="input-group">
                          <label>New Password</label>
                          <div className="input-wrapper">
                              <input type={showPassword ? "text" : "password"} value={newPassword}
                                onChange={(e) => setNewPassword(e.target.value)} placeholder="New Password" required disabled={loading}
                              />
                              <button type="button" className="toggle-pass" onClick={() => setShowPassword(!showPassword)}>
                                {showPassword ? <EyeOff size={16}/> : <Eye size={16}/>}
                              </button>
                          </div>
                       </div>
                       <div className="input-group">
                          <label>Confirm Password</label>
                          <input type={showPassword ? "text" : "password"} value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)} placeholder="Confirm Password" required disabled={loading}
                          />
                       </div>
                   </div>

                   <button onClick={handleResetPassword} className="primary-btn" disabled={loading}>
                      {loading ? 'Verifying...' : 'Reset Password'}
                   </button>
                </div>

                {/* Resend Link - OUTSIDE the card */}
                <div className="resend-text-container">
                   <span className="resend-label">Didn't receive the email?</span>
                   <button onClick={handleResendCode} className="resend-link-text" disabled={resendDisabled || loading}>
                      {resendDisabled ? 'Wait to resend' : 'Resend Code'}
                   </button>
                </div>
             </div>
          )}
        </div>

        {/* RIGHT COLUMN */}
        <div className="reset-right-col">
           <div className="info-panel">
              <h3><Info size={18} className="text-teal"/> Why we need this</h3>
              <p>Security is our top priority. Verifying your email ensures that you are the legitimate owner of the provided contact information.</p>
           </div>
        </div>

      </div>
    </div>
  );
};

export default ResetPassword;